import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClientFormContext, submitClientForm } from '../api/jobs';
import type { ClientFormContext } from '../types';
import { Spinner, ErrorBanner, toMessage } from '../components/common';
import { PublicBrand } from '../components/PublicBrand';

/**
 * The client-facing form page, for both kinds of client form.
 *
 * 'queries'  the clarification questions. Before this the client replied by email
 *            and nothing parsed it, so answers never reached the workpaper.
 * 'intake'   the general information questionnaire, replacing a spreadsheet round
 *            trip that on a real engagement came back entirely blank.
 *
 * The intake branch was rebuilt on 2026-09-07 after a design review. It had never
 * opted into the card system its two sibling public pages use: it set
 * `public-card` (which is `text-align: center`, meant only for the brand lockup)
 * on the whole form, used a `secondary` button class that does not exist, and
 * relied on placeholders as labels, so the moment anyone typed they lost the
 * question. Everything below follows Etoe.tsx and Upload.tsx instead.
 */

/** One director, with a row per financial year underneath. */
interface DirectorYear {
  fy: string;
  hoursPerWeek: string;
  salaryInclSuper: string;
  dividends: string;
  vehicleAllowance: string;
}
interface Director {
  name: string;
  position: string;
  responsibilities: string;
  years: DirectorYear[];
}
interface Shareholder {
  name: string;
  sharesPct: string;
}
interface TopClient {
  year: string;
  name: string;
  revenue: string;
}

/**
 * One labelled field.
 *
 * Defined at module scope, and it matters. While this lived inside ClientForm its
 * function identity changed on every render, so React saw a different component
 * type in the same position, unmounted the subtree and mounted a fresh one. The
 * input was destroyed and recreated between keystrokes: focus jumped away after
 * every character and the page scrolled to wherever the new node landed. Typing a
 * director's name was impossible.
 *
 * The error text is a prop rather than a closure over `invalid`, which is what
 * allows the hoist.
 */
function Field({
  id,
  label,
  hint,
  optional,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`field${error ? ' invalid' : ''}`}>
      <label htmlFor={id}>
        {label}
        {optional ? <span className="field-optional"> (optional)</span> : null}
      </label>
      {children}
      {hint ? <div className="field-hint">{hint}</div> : null}
      {error ? <div className="field-error">{error}</div> : null}
    </div>
  );
}

/**
 * Enter inside a table cell moves DOWN a row instead of submitting the form.
 *
 * HTML fires implicit submission on Enter in any single-line input, and these tables
 * are filled in by people trained on Excel. Pressing Enter used to either shout
 * validation errors mid-fill or submit a half-finished questionnaire. Shift+Enter
 * goes up. Tab is untouched and still walks the row.
 *
 * Deliberately NOT arrow keys: Left and Right already move the caret, and clients
 * correct typos in salary figures constantly.
 */
function cellKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  const cell = e.currentTarget.closest('td, th');
  const row = e.currentTarget.closest('tr');
  if (!cell || !row?.parentElement) return;
  const col = Array.prototype.indexOf.call(row.children, cell);
  const rows = Array.from(row.parentElement.children) as HTMLElement[];
  const here = rows.indexOf(row as HTMLElement);
  const step = e.shiftKey ? -1 : 1;
  for (let i = here + step; i >= 0 && i < rows.length; i += step) {
    const target = rows[i].children[col]?.querySelector('input');
    if (target && !(target as HTMLInputElement).disabled) {
      (target as HTMLInputElement).focus();
      (target as HTMLInputElement).select();
      return;
    }
  }
  // Last row in the column: stay put rather than submitting.
  e.currentTarget.blur();
}

/**
 * "FY25" -> "Financial year 2025". Used where there is room for it.
 *
 * Clients are not valuers. "FY25" is shorthand the practice uses internally, and on a
 * form somebody fills in once it is one more thing to decode. Left as-is where the
 * column is narrow, and anything that is not an FY label passes through untouched.
 */
function fyLabel(fy: string): string {
  const m = /^FY\s*(\d{2}|\d{4})$/i.exec(String(fy ?? '').trim());
  if (!m) return fy;
  const yr = m[1].length === 2 ? `20${m[1]}` : m[1];
  return `Financial year ${yr}`;
}

/** Keep only what can be part of a number, so a pasted "$2,000,000" still lands. */
function stripNumeric(v: string): string {
  return String(v ?? '').replace(/[^0-9.\-]/g, '');
}

type NumKind = 'money' | 'percent' | 'hours';

/** "594492" -> "$594,492", "60" -> "60%", "50" -> "50 h". Blank stays blank. */
function formatNumeric(raw: string, kind: NumKind): string {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  const n = Number(stripNumeric(t));
  if (!Number.isFinite(n)) return t;
  if (kind === 'money') return `$${n.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
  if (kind === 'percent') return `${Number(n.toFixed(2))}%`;
  return `${Number(n.toFixed(2))} h`;
}

/**
 * A numeric cell that reads as a figure and types as a number.
 *
 * Formatted while you are not in it, raw while you are. Formatting the live value as
 * someone types fights the caret: insert a thousands separator mid-entry and the
 * cursor jumps, which is how "594492" becomes "594,4926". Swapping on focus and blur
 * gives the readable version where it matters, on the filled-in form somebody checks
 * before submitting, and gets out of the way during entry.
 */
function NumCell({
  value,
  onChange,
  kind,
  label,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  kind: NumKind;
  label: string;
  invalid?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      aria-label={label}
      inputMode="decimal"
      className="money"
      data-invalid={invalid ? 'true' : undefined}
      value={focused ? value : formatNumeric(value, kind)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={cellKeyDown}
      onChange={(e) => onChange(stripNumeric(e.target.value))}
    />
  );
}

/**
 * The per-year questions for a director, as table rows.
 *
 * `short` keeps the existing validation keys (d0y1-salary and so on) working, so the
 * error handling did not have to be rewritten alongside the layout.
 */
const DIRECTOR_ROWS: {
  key: 'hoursPerWeek' | 'salaryInclSuper' | 'dividends' | 'vehicleAllowance';
  short: string;
  label: string;
  optional?: boolean;
  kind: NumKind;
}[] = [
  { key: 'hoursPerWeek', short: 'hours', label: 'Hours per week', kind: 'hours' },
  { key: 'salaryInclSuper', short: 'salary', label: 'Salary including superannuation', kind: 'money' },
  { key: 'dividends', short: 'div', label: 'Dividends', kind: 'money' },
  { key: 'vehicleAllowance', short: 'veh', label: 'Vehicle allowance', optional: true, kind: 'money' },
];

function blankDirector(years: string[]): Director {
  return {
    name: '',
    position: '',
    responsibilities: '',
    years: years.map((fy) => ({
      fy,
      hoursPerWeek: '',
      salaryInclSuper: '',
      dividends: '',
      vehicleAllowance: '',
    })),
  };
}

/**
 * A typed revenue figure as a number, tolerant of what people actually type.
 *
 * Clients enter "$2,000,000", "2 000 000" and "2000000" interchangeably, and a total
 * that ignores two of those three reads as a bug to the person looking at it.
 */
function revenueOf(c: { revenue?: string }): number {
  const n = Number(String(c.revenue ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function ClientForm({
  embedded = false,
  onDone,
}: { embedded?: boolean; onDone?: () => void } = {}) {
  // `embedded` is the Collect information portal rendering the questionnaire as one
  // of two tabs; it draws the brand header itself.
  const { token = '' } = useParams();
  const [ctx, setCtx] = useState<ClientFormContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState('');

  const [answers, setAnswers] = useState<string[]>([]);
  // Somewhere for a reply that does not fit the boxes. Clients write prose.
  const [note, setNote] = useState('');
  const [directors, setDirectors] = useState<Director[]>([]);
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [topClients, setTopClients] = useState<TopClient[]>([]);
  const [fullTime, setFullTime] = useState('');
  const [partTime, setPartTime] = useState('');
  const [hasLeave, setHasLeave] = useState('');
  const [leaveAmount, setLeaveAmount] = useState('');
  const [contractsInPlace, setContractsInPlace] = useState('');
  // Retail, hospitality and healthcare genuinely have no top five. Without this
  // they either invent a client or abandon the form.
  const [noConcentration, setNoConcentration] = useState(false);
  const [contractsType, setContractsType] = useState('');

  // Field-level errors, keyed so the first one can be focused. Set only on a
  // submit attempt: nobody should be shouted at mid-keystroke on a form about
  // their own salary.
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [attempted, setAttempted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const load = useCallback(async () => {
    try {
      const c = await getClientFormContext(token);
      setCtx(c);
      if (c.kind === 'queries') {
        setAnswers(new Array(c.queries?.length ?? 0).fill(''));
      } else {
        const years = c.financialYears?.length ? c.financialYears : [''];
        setDirectors([blankDirector(years)]);
        // Three rows up front, not five. Most WA SMEs this size have two or three
        // owners, and five empty rows reads as five things you have failed to fill in.
        // Add a row covers the rest, and the percentages have to reach 100 either way.
        setShareholders([0, 1, 2].map(() => ({ name: '', sharesPct: '' })));
        // Five fixed rows for each of the last two years, matching WABV's own form.
        // Seeded up front rather than added a row at a time, so the client sees the
        // shape of what is being asked and can fill it in any order.
        setTopClients(
          years.slice(-2).flatMap((y) => [0, 1, 2, 3, 4].map(() => ({ year: y, name: '', revenue: '' }))),
        );
      }
    } catch (err) {
      setLoadError(toMessage(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Mirrors the server's own gap list, so the two can never disagree. */
  function validate(): Record<string, string> {
    const bad: Record<string, string> = {};
    if (!name.trim()) bad['yourname'] = 'Please tell us who is completing this.';
    if (ctx?.kind === 'queries') {
      // At least one answer, not all of them. Requiring every box meant a client
      // who wrote one paragraph covering three questions could not submit, and
      // nothing told them why: the form simply did nothing when they pressed it.
      if (!answers.some((a) => a.trim()) && !note.trim()) {
        bad.q0 = 'Please answer at least one query, or use the box at the end.';
      }
      return bad;
    }
    // Mirrors client-form-submit exactly. What "complete" means comes from the
    // Owners' Involvement tab: Workpapers sums the salary rows for the director
    // add-back, and the valuer's market-rate working needs the hours.
    if (!directors.some((d) => d.name.trim())) {
      bad['director-0-name'] = 'Please add at least one director.';
    }
    directors.forEach((d, i) => {
      const started =
        d.name.trim() || d.position.trim() ||
        d.years.some((y) => y.salaryInclSuper.trim() || y.hoursPerWeek.trim() || y.dividends.trim());
      if (!started) return;
      if (!d.name.trim()) bad[`director-${i}-name`] = 'A name is needed for this director.';
      if (!d.position.trim()) bad[`director-${i}-position`] = 'Please give their position.';
      const done = (y: DirectorYear) =>
        Boolean(y.hoursPerWeek.trim() && y.salaryInclSuper.trim() && y.dividends.trim());
      const begun = (y: DirectorYear) =>
        Boolean(y.hoursPerWeek.trim() || y.salaryInclSuper.trim() || y.dividends.trim());
      if (!d.years.some(done)) {
        // A director appointed mid-period genuinely has no earlier year, so only
        // ONE complete year is required. Point at the most recent to fill in.
        const last = d.years.length - 1;
        bad[`d${i}y${last}-salary`] =
          'At least one financial year needs hours, salary and dividends. Enter 0 where there were none.';
      }
      d.years.forEach((y, yi) => {
        if (begun(y) && !done(y)) {
          if (!y.hoursPerWeek.trim()) bad[`d${i}y${yi}-hours`] = 'Needed once this year is started.';
          if (!y.salaryInclSuper.trim()) bad[`d${i}y${yi}-salary`] = 'Needed once this year is started.';
          if (!y.dividends.trim()) bad[`d${i}y${yi}-div`] = 'Needed once this year is started. Enter 0 if none.';
        }
      });
    });
    if (!shareholders.some((s) => s.name.trim())) {
      bad['shareholders'] = 'Please list at least one shareholder.';
    } else if (shareholders.some((s) => s.name.trim() && !s.sharesPct.trim())) {
      bad['shareholders'] = 'Please give the percentage held for each shareholder you have named.';
    } else if (Math.abs(shareTotal - 100) > 0.01) {
      // Worth blocking on: a shareholding that does not add to 100 means either a
      // missing owner or a typo, and both change who the valuation is for.
      const t = Number(shareTotal.toFixed(2));
      bad['shareholders'] =
        t < 100
          ? `The percentages add to ${t}%, which is ${Number((100 - t).toFixed(2))}% short of 100. Add the missing owner, or correct the figures.`
          : `The percentages add to ${t}%, which is ${Number((t - 100).toFixed(2))}% over 100. Check the figures.`;
    }
    if (!fullTime.trim()) bad['fulltime'] = 'Please give a number. Enter 0 if there are none.';
    if (!partTime.trim()) bad['parttime'] = 'Please give a number. Enter 0 if there are none.';
    if (!hasLeave) bad['hasleave'] = 'Please answer yes or no.';
    if (hasLeave === 'Yes' && !leaveAmount.trim()) {
      bad['leaveamount'] = 'Please give the amount to date.';
    }
    // Keyed on the section rather than a row, because the rows are now a fixed grid
    // and pointing at "client 3" in a table of ten is less use than one message under
    // the tables.
    if (!noConcentration && !topClients.some((c) => c.name.trim())) {
      bad['topclients'] =
        'Please name at least one client, or tick the box below if no single client stands out.';
    } else if (topClients.some((c) => c.name.trim() && !c.revenue.trim())) {
      bad['topclients'] = 'Please give the revenue for each client you have named.';
    }
    if (!contractsInPlace) bad['contracts'] = 'Please answer yes or no.';
    return bad;
  }

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setAttempted(true);
    const bad = validate();
    setInvalid(bad);
    if (Object.keys(bad).length) {
      setError('Some answers are still needed. They are marked below.');
      const first = formRef.current?.querySelector<HTMLElement>('[data-invalid="true"]');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.focus?.();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload =
        ctx?.kind === 'queries'
          ? { submittedByName: name, answers, note }
          : {
              submittedByName: name,
              // The sheet asks for name and position once per director with the
              // years underneath. The API takes one row per director-year, so
              // flatten here and leave the wire format untouched.
              directors: directors
                .filter((d) => d.name.trim())
                .flatMap((d) =>
                  d.years.map((y) => ({
                    financialYear: y.fy,
                    name: d.name,
                    position: d.position,
                    responsibilities: d.responsibilities,
                    hoursPerWeek: y.hoursPerWeek,
                    salaryInclSuper: y.salaryInclSuper,
                    dividends: y.dividends,
                    vehicleAllowance: y.vehicleAllowance,
                  })),
                ),
              shareholders: shareholders.filter((s) => s.name.trim()),
              topClients: topClients.filter((c) => c.name.trim()),
              noClientConcentration: noConcentration,
              employeesFullTime: fullTime,
              employeesPartTime: partTime,
              hasLeaveLiabilities: hasLeave,
              leaveLiabilitiesAmount: leaveAmount,
              contractsInPlace,
              contractsType,
            };
      await submitClientForm(token, payload);
      // Inside the Collect portal this is step one of two, so the parent moves the
      // client to the documents tab rather than telling them they have finished.
      if (onDone) onDone();
      else setDone(true);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /** Re-check one field once it has been touched, but only after a first attempt. */
  function clearIfFixed(key: string, value: string) {
    if (!attempted || !invalid[key] || !value.trim()) return;
    setInvalid((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  if (loadError) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
        </div>
        <div className="card">
          <ErrorBanner message={loadError} />
        </div>
      </div>
    );
  }
  if (!ctx) return <Spinner />;

  if (done) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
        </div>
        <div className="card">
          <h1>Thank you</h1>
          <p>
            We have received your {ctx.kind === 'queries' ? 'answers' : 'information'} for{' '}
            {ctx.entityName}. There is nothing further for you to do. We will be in touch if
            anything else is needed.
          </p>
        </div>
      </div>
    );
  }

  const years = ctx.financialYears?.length ? ctx.financialYears : [''];
  // Concentration is asked for the last two years only. Ask for FY24 and FY25 on an
  // FY23-25 engagement, FY25 and FY26 on an FY24-26 one.
  const clientYears = years.slice(-2);
  const shareTotal = shareholders.reduce((sum, sh) => {
    const n = Number(String(sh.sharesPct ?? '').replace(/[^0-9.-]/g, ''));
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  const money = 'Whole dollars for the full financial year.';

  return (
    <div
      className={
        embedded
          ? undefined
          // Both client forms share the wider wrapper: the questionnaire needs it for
          // its grids, and the queries form matches so the two pages a client sees
          // back to back feel like the same application. The proposal stays narrow,
          // because that one IS prose somebody reads and signs.
          : 'public-wrap public-wrap--wide'
      }
    >
      {embedded ? null : (
        <div className="card public-card">
          <PublicBrand />
        </div>
      )}

      <form ref={formRef} onSubmit={submit} className="client-form" noValidate>
        <div className="card">
          <h1>{ctx.kind === 'queries' ? 'Clarification queries' : 'General information'}</h1>
          <p className="lead">For {ctx.entityName}.</p>
          {ctx.kind === 'queries' ? (
            <p>
              We have reviewed the financial information you sent through. Answer what you can
              below. Short answers are fine, and if one reply covers several queries, or you
              would rather just write back in your own words, use the box at the end. If
              something needs to come from your accountant, say so and we will follow it up.
            </p>
          ) : (
            <p>
              Alongside the financial statements we need some background on the business.
              Everything is needed unless it is marked optional.
            </p>
          )}
          <div className="secure-note">
            <span className="secure-note-icon" aria-hidden="true">🔒</span>
            <span>
              Nothing is saved until you press Submit, and this link stays valid until the form is
              complete, so you can come back to it. Your answers travel over an encrypted
              connection straight to WA Business Valuations. When you submit, we record the
              date and time, your IP address and your browser, as a record of who provided
              this information.
            </span>
          </div>
        </div>

        {error ? (
          <div className="card">
            <ErrorBanner message={error} />
          </div>
        ) : null}

        {ctx.kind === 'queries' ? (
          <div className="card">
            {/* Questions are NOT marked optional: the valuer wants an answer to each
                one, and marking all eight optional invites a client to skip the lot.
                What changed is that the form no longer REFUSES a partial reply. The
                intro explains that. */}
            {(ctx.queries ?? []).map((q, i) => (
              <Field key={i} id={`q${i}`} label={`${i + 1}. ${q.area}`} error={invalid[`q${i}`]}>
                <p className="question-text">{q.query}</p>
                <textarea
                  id={`q${i}`}
                  rows={4}
                  data-invalid={invalid[`q${i}`] ? 'true' : undefined}
                  value={answers[i] ?? ''}
                  onChange={(e) => {
                    const next = [...answers];
                    next[i] = e.target.value;
                    setAnswers(next);
                    clearIfFixed(`q${i}`, e.target.value);
                  }}
                />
              </Field>
            ))}
            {/* Clients reply in prose. Without somewhere legitimate to put it,
                a paragraph answering three questions at once has to be forced
                into one numbered box, or the form gets abandoned. */}
            <Field
              id="note"
              label="Anything else, or a reply in your own words"
              optional
              hint="If it is easier to write one answer covering several queries, do that here. Leave the boxes above blank if you have."
              error={invalid.note}
            >
              <textarea
                id="note"
                rows={8}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  clearIfFixed('note', e.target.value);
                }}
              />
            </Field>
          </div>
        ) : (
          <>
            <div className="card">
              <h2>Directors</h2>
              <p className="lead">
                One card per director. Their name and role once, then what they were paid
                in each financial year.
              </p>
              {directors.map((d, di) => {
                const upd = (patch: Partial<Director>) =>
                  setDirectors(directors.map((x, j) => (j === di ? { ...x, ...patch } : x)));
                const updYear = (yi: number, patch: Partial<DirectorYear>) =>
                  upd({ years: d.years.map((y, j) => (j === yi ? { ...y, ...patch } : y)) });
                return (
                  <div
                    key={di}
                    className={`entity-card${invalid[`director-${di}-name`] ? ' invalid' : ''}`}
                  >
                    <div className="entity-head">
                      <h3>Director {di + 1}</h3>
                      {directors.length > 1 ? (
                        <button
                          type="button"
                          className="btn-secondary btn-mini-2"
                          onClick={() => setDirectors(directors.filter((_, j) => j !== di))}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <Field id={`director-${di}-name`} label="Full name" error={invalid[`director-${di}-name`]}>
                      <input
                        id={`director-${di}-name`}
                        autoComplete="off"
                        data-invalid={invalid[`director-${di}-name`] ? 'true' : undefined}
                        value={d.name}
                        onChange={(e) => {
                          upd({ name: e.target.value });
                          clearIfFixed(`director-${di}-name`, e.target.value);
                        }}
                      />
                    </Field>
                    <Field id={`director-${di}-position`}
                            data-invalid={invalid[`director-${di}-position`] ? 'true' : undefined} label="Position" error={invalid[`director-${di}-position`]}>
                      <input
                        id={`director-${di}-position`}
                        value={d.position}
                        onChange={(e) => upd({ position: e.target.value })}
                      />
                    </Field>
                    <Field
                      id={`director-${di}-resp`}
                      label="Main responsibilities"
                      optional
                      hint="What they actually do day to day." error={invalid[`director-${di}-resp`]}>
                      <input
                        id={`director-${di}-resp`}
                        value={d.responsibilities}
                        onChange={(e) => upd({ responsibilities: e.target.value })}
                      />
                    </Field>
                    {/*
                      One row per thing asked, one column per financial year, instead of
                      the same four fields repeated down the page once per year. Three
                      years of a single director was twelve stacked inputs and most of a
                      phone screen; this is a grid you can read across.
                    */}
                    <div className="table-scroll">
                      <table className="clients-table director-table">
                        <thead>
                          <tr>
                            <th scope="col">Category</th>
                            {d.years.map((y, yi) => (
                              <th scope="col" key={y.fy || yi}>{y.fy}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {DIRECTOR_ROWS.map((row) => (
                            <tr key={row.key}>
                              <th scope="row">
                                {row.label}
                                {row.optional ? <span className="opt"> (optional)</span> : null}
                              </th>
                              {d.years.map((y, yi) => (
                                <td key={y.fy || yi}>
                                  <NumCell
                                    kind={row.kind}
                                    label={`${d.name || `Director ${di + 1}`} ${row.label}${y.fy ? ` ${y.fy}` : ''}`}
                                    invalid={Boolean(invalid[`d${di}y${yi}-${row.short}`])}
                                    value={y[row.key]}
                                    onChange={(v) => {
                                      updYear(yi, { [row.key]: v } as Partial<DirectorYear>);
                                      clearIfFixed(`d${di}y${yi}-${row.short}`, v);
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="field-hint">Whole dollars for the full financial year. Enter 0 where there were none.</p>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDirectors([...directors, blankDirector(years)])}
              >
                Add another director
              </button>
            </div>

            <div className="card">
              <h2>Shareholders</h2>
              {/*
                A table rather than a stack of cards, for the same reason as the client
                one: the numbers have to add to 100, and a total you cannot see is a
                total nobody checks. Unlike that table the percentage here is TYPED, not
                derived, so only the total row is calculated.
              */}
              <p className="lead">Who owns the business, and how much of it. The percentages must add to exactly 100 before you can submit.</p>
              <table className="clients-table">
                <thead>
                  <tr>
                    <th scope="col">Shareholder</th>
                    <th scope="col">% Total</th>
                  </tr>
                </thead>
                <tbody>
                  {shareholders.map((sh, i) => {
                    const upd = (patch: Partial<Shareholder>) =>
                      setShareholders(shareholders.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            aria-label={`Shareholder ${i + 1} name`}
                            placeholder={`Shareholder ${i + 1}`}
                            onKeyDown={cellKeyDown}
                            value={sh.name}
                            onChange={(e) => {
                              upd({ name: e.target.value });
                              clearIfFixed('shareholders', e.target.value);
                            }}
                          />
                        </td>
                        <td>
                          <NumCell
                            kind="percent"
                            label={`Shareholder ${i + 1} percentage`}
                            value={sh.sharesPct}
                            onChange={(v) => {
                              upd({ sharesPct: v });
                              clearIfFixed('shareholders', v);
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <th scope="row">Total</th>
                    {/*
                      Shows what the numbers actually add to, not the 100 they are
                      supposed to. A row that always reads 100% would hide the one thing
                      this total exists to catch.
                    */}
                    <td className={`calc${shareTotal > 0 && Math.abs(shareTotal - 100) > 0.01 ? ' calc-warn' : ''}`}>
                      {shareTotal > 0 ? `${Number(shareTotal.toFixed(2))}%` : ''}
                    </td>
                  </tr>
                </tbody>
              </table>
              {invalid['shareholders'] ? <p className="field-error">{invalid['shareholders']}</p> : null}
              <div className="btn-row">
                <button
                  type="button"
                  className="btn-secondary btn-mini-2"
                  onClick={() => setShareholders([...shareholders, { name: '', sharesPct: '' }])}
                >
                  Add a row
                </button>
              </div>
            </div>

            <div className="card">
              <h2>Employees and leave</h2>
              <Field id="fulltime" label="Full time employees" error={invalid['fulltime']}>
                <input
                  id="fulltime"
                  inputMode="decimal"
                  data-invalid={invalid['fulltime'] ? 'true' : undefined}
                  value={fullTime}
                  onChange={(e) => {
                    setFullTime(e.target.value);
                    clearIfFixed('fulltime', e.target.value);
                  }}
                />
              </Field>
              <Field id="parttime" label="Part time and casual employees" error={invalid['parttime']}>
                <input
                  id="parttime"
                  inputMode="decimal"
                  data-invalid={invalid['parttime'] ? 'true' : undefined}
                  value={partTime}
                  onChange={(e) => setPartTime(e.target.value)}
                />
              </Field>
              <Field id="hasleave" label="Does the business carry annual or long service leave liabilities?" error={invalid['hasleave']}>
                <select
                  id="hasleave"
                  data-invalid={invalid['hasleave'] ? 'true' : undefined}
                  value={hasLeave}
                  onChange={(e) => {
                    setHasLeave(e.target.value);
                    clearIfFixed('hasleave', e.target.value);
                  }}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              {hasLeave === 'Yes' ? (
                <Field id="leaveamount" label="How much, to date?" hint={money} error={invalid['leaveamount']}>
                  <input
                    id="leaveamount"
                    inputMode="decimal"
                    data-invalid={invalid['leaveamount'] ? 'true' : undefined}
                    className="money"
                    value={leaveAmount}
                    onChange={(e) => setLeaveAmount(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>

            {/*
              Two fixed tables, one per financial year, matching the layout WABV's own
              questionnaire uses and that their clients have filled in before. The old
              shape was one card per client, each with its own year dropdown, which meant
              ten cards and ten dropdown selections to say the same thing, no totals, and
              three clients filling a phone screen.

              Only the LAST TWO years in scope are asked for. A valuation cares about
              concentration now, not four years ago, and asking for every year in the
              engagement is how the section got left blank on the real Blue Jay job.
            */}
            <div className="card">
              <h2>Top 5 clients</h2>
              <p className="lead">
                Your five biggest clients by revenue, for the last two financial years.
                Totals and percentages work themselves out as you go.
              </p>
              {clientYears.map((y) => {
                const rows = topClients.filter((c) => c.year === y);
                const total = rows.reduce((sum, c) => sum + revenueOf(c), 0);
                return (
                  <table className="clients-table" key={y}>
                    <thead>
                      <tr>
                        <th scope="col">{fyLabel(y)}</th>
                        <th scope="col">Revenue</th>
                        <th scope="col">% Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => {
                        const i = topClients.indexOf(c);
                        const upd = (patch: Partial<TopClient>) =>
                          setTopClients(topClients.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                        const rev = revenueOf(c);
                        // Blank rather than 0% until there is something to be a percentage
                        // of, so an empty table does not read as five clients worth nothing.
                        // A genuine client rounding to 0% reads as "this one does not
                        // count". Say "<1%" so a small but real client still shows.
                        const share = total > 0 && rev > 0 ? (rev / total) * 100 : 0;
                        const pct = share === 0 ? '' : share < 0.5 ? '<1%' : `${Math.round(share)}%`;
                        return (
                          <tr key={i}>
                            <td>
                              <input
                                aria-label={`${y} client ${rows.indexOf(c) + 1} name`}
                                placeholder={`Client ${rows.indexOf(c) + 1}`}
                                onKeyDown={cellKeyDown}
                                value={c.name}
                                onChange={(e) => {
                                  upd({ name: e.target.value });
                                  clearIfFixed('topclients', e.target.value);
                                }}
                              />
                            </td>
                            <td>
                              <NumCell
                                kind="money"
                                label={`${y} client ${rows.indexOf(c) + 1} revenue`}
                                value={c.revenue}
                                onChange={(v) => upd({ revenue: v })}
                              />
                            </td>
                            <td className="calc">{pct}</td>
                          </tr>
                        );
                      })}
                      <tr className="total-row">
                        <th scope="row">Total</th>
                        <td className="calc">{total > 0 ? `$${total.toLocaleString('en-AU')}` : ''}</td>
                        <td className="calc">{total > 0 ? '100%' : ''}</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })}
              {invalid['topclients'] ? <p className="field-error">{invalid['topclients']}</p> : null}
              <label className="tickbox">
                <input
                  type="checkbox"
                  checked={noConcentration}
                  onChange={(e) => {
                    setNoConcentration(e.target.checked);
                    if (e.target.checked) clearIfFixed('topclients', 'x');
                  }}
                />
                <span>
                  No single client accounts for a meaningful share of our revenue. Our income
                  is spread across many customers.
                </span>
              </label>
              <p className="field-hint">
                Whole dollars for the full financial year. Leave a row blank if you have
                fewer than five, and if no single client stands out, say so in the contracts
                box below.
              </p>
            </div>

            <div className="card">
              <h2>Client contracts</h2>
              <Field id="contracts" label="Are there contracts in place with your clients?" error={invalid['contracts']}>
                <select
                  id="contracts"
                  data-invalid={invalid['contracts'] ? 'true' : undefined}
                  value={contractsInPlace}
                  onChange={(e) => setContractsInPlace(e.target.value)}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              {contractsInPlace === 'Yes' ? (
                <Field id="contractstype" label="Contract description" hint="For example ongoing, fixed term, or per project." error={invalid['contractstype']}>
                  <input
                    id="contractstype"
                    value={contractsType}
                    onChange={(e) => setContractsType(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>
          </>
        )}

        <div className="card">
          <Field id="yourname" label="Your name" error={invalid['yourname']}>
            <input
              id="yourname"
              autoComplete="name"
              data-invalid={invalid['yourname'] ? 'true' : undefined}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearIfFixed('yourname', e.target.value);
              }}
            />
          </Field>
          <div className="btn-row">
            <button type="submit" disabled={busy}>
              {busy ? 'Saving.' : embedded && ctx.kind === 'intake' ? 'Next' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
